import { FunctionComponent, useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import { TextField } from "@mui/material";

import { Link } from "react-router-dom";

import GoogleMapsIcon from "@assets/icons/google-maps.svg?url";

import { ExternalLink } from "@/components/ExternalLink/ExternalLink";

import { Loader, Modal } from "@/components";

import { Map } from "@/pages/Private/pages/Map/schema/map";

import { Table, TableColumn } from "@/components/Table/Table";

import { useDeleteMapMutation } from "@/pages/Private/redux/map/map.api";
import { useLazyGetListQuery } from "@/pages/Private/redux/list/list.api";

import { useDeleteJobMutation } from "@/pages/Private/redux/job/job.api";

import { useDeleteCsvEnrichmentMutation } from "@/pages/Private/redux/csvEnrichment/csvEnrichment.api";

import { ListSourceType } from "@/enum/list.enum";

import { useDeleteLiEventMutation } from "@/pages/Private/redux/liEvent/liEvent.api";

import { useDeleteLiPostMutation } from "@/pages/Private/redux/liPost/liPost.api";

import { useDeleteLiCompanyActivityMutation } from "@/pages/Private/redux/liCompanyActivity/liCompanyActivity.api";

import { useDeleteLiProfileMutation } from "@/pages/Private/redux/liProfile/liProfile.api";

import { useDeleteLiPeopleMutation } from "@/pages/Private/redux/liPeople/liPeople.api";

import { useDeleteLiProfileVisitsMutation } from "@/pages/Private/redux/liProfileVisits/liProfileVisits.api";

import { useDeleteLiSnMutation } from "@/pages/Private/redux/liSn/liSn.api";

import { useDeleteFundingMaMutation } from "@/pages/Private/redux/fundingMa/fundingMa.api";

import { useDeleteSwarmMutation } from "@/pages/Private/redux/swarm/swarm.api";

import { useDeleteCompanyEnrichMutation } from "@/pages/Private/redux/companyEnrich/companyEnrich.api";

import { useDeleteTrackerFileMutation } from "@/pages/Private/redux/profileTracker/profileTracker.api";

import {
	useDeleteParallelAiMutation,
	useTriggerParallelAiMutation,
} from "@/pages/Private/redux/parallelAi/parallelAi.api";

import { useHasPermissions } from "@/utils/useHasPermissions";
import { PermissionRoles } from "@/enum";
import { useSnackBar } from "@/utils/useSnackbar";

import { XlsStatisticsModal } from "./XlsStatisticsModal/XlsStatisticsModal";
import { useSourcePreview } from "./useSourcePreview";

import { List, TrackerFileInfo } from "../schema/list";
import { Job } from "../../Job/schema/job";
import { IMapResponseItem } from "../../Map/interface";
import { LiEvent } from "../../LiEvent/schema/liEvent";
import { LiCompanyActivity } from "../../LiCompanyActivity/schema/liCompanyActivity";
import { LiPost } from "../../LiPost/schema/liPost";
import { LiProfileVisits } from "../../LiProfileVisits/schema/liProfileVisits";
import { CsvEnrichment } from "../../CsvEnrichment/schema/csvEnrichment";
import { LiProfile } from "../../LiProfile/schema/liProfile";
import { getCustomTableColumns } from "../utils/addedSearchTableConfigs";

export interface AddedSourceListProps {
	list: List;
	downloadIsLoading?: number | null;
	handleDownload?: (csvEnrichment: CsvEnrichment) => Promise<void>;
	handleTrackerDownload?: (trackerFile: TrackerFileInfo) => Promise<void>;
	handleSelect: (map: Map | Job | LiCompanyActivity | LiPost | LiProfileVisits) => void;
	handleDuplicate?: (map: Map | Job | LiCompanyActivity | LiPost | LiProfileVisits) => void;
}

export const AddedSourceList: FunctionComponent<AddedSourceListProps> = ({
	list,
	handleSelect,
	handleDuplicate,
	downloadIsLoading,
	handleDownload,
	handleTrackerDownload,
}) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`list.sources.${key}`), [t]);

	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	// PREC-3870: the uploaded source whose after-enrichment statistics are open, if any.
	const [statisticsSource, setStatisticsSource] = useState<{ id: number; name?: string } | null>(
		null
	);
	const [selectedMap, setSelectedMap] = useState<
		Map | Job | LiEvent | LiPost | LiCompanyActivity | CsvEnrichment | LiProfile | null
	>(null);
	const [deleteMap, { isLoading: isDeleteLoading }] = useDeleteMapMutation();
	const [deleteJob, { isLoading: jobIsDeleteLoading }] = useDeleteJobMutation();
	const [deleteLiEvent, { isLoading: liEventIsDeleteLoading }] = useDeleteLiEventMutation();
	const [deleteLiPost, { isLoading: liPostIsDeleteLoading }] = useDeleteLiPostMutation();
	const [deleteLiProfile, { isLoading: liProfileIsDeleteLoading }] = useDeleteLiProfileMutation();
	const [deleteLiPeople, { isLoading: LiPeopleIsDeleteLoading }] = useDeleteLiPeopleMutation();
	const [deleteLiSn, { isLoading: liSnIsDeleteLoading }] = useDeleteLiSnMutation();
	const [deleteFundingMa, { isLoading: fundingMaIsDeleteLoading }] = useDeleteFundingMaMutation();
	const [deleteSwarm, { isLoading: swarmIsDeleteLoading }] = useDeleteSwarmMutation();
	const [deleteCompanyEnrich, { isLoading: companyEnrichIsDeleteLoading }] =
		useDeleteCompanyEnrichMutation();
	const [deleteLiCompanyActivity, { isLoading: liCompanyActivityIsDeleteLoading }] =
		useDeleteLiCompanyActivityMutation();
	const [deleteCsvEnrichment, { isLoading: csvEnrichmentIsDeleteLoading }] =
		useDeleteCsvEnrichmentMutation();

	const [deleteLiProfileVisits, { isLoading: liProfileVisitsIsDeleteLoading }] =
		useDeleteLiProfileVisitsMutation();
	const [deleteTrackerFile, { isLoading: trackerFileIsDeleteLoading }] =
		useDeleteTrackerFileMutation();
	const [deleteParallelAi, { isLoading: parallelAiIsDeleteLoading }] =
		useDeleteParallelAiMutation();
	const [triggerParallelAi] = useTriggerParallelAiMutation();

	const { hasPermissions } = useHasPermissions();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	// Only GLOBAL_ADMINs may force an immediate monitor run (matches the API permission).
	const canTriggerParallelAi = hasPermissions([PermissionRoles.GLOBAL_ADMIN]);

	const handleTriggerParallelAi = useCallback(
		async (row: { id: number }) => {
			try {
				const result = await triggerParallelAi(row.id).unwrap();

				handleOpenSnackbar(result.message);
			} catch (err) {
				console.error(err);
				handleOpenSnackbar(t("errors.wentWrong"));
			}
		},
		[triggerParallelAi, handleOpenSnackbar, t]
	);

	const [refreshList] = useLazyGetListQuery();
	const {
		isPreviewOpen,
		isPreviewLoading,
		previewRows,
		openPreview: handlePreviewModal,
		closePreview,
	} = useSourcePreview();

	const previewColumns: TableColumn[] = [
		{
			title: "name",
			uid: "name",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: IMapResponseItem) => (
				<div className="flex items-center">
					{row.name}
					<Link target="_blank" to={row.website ?? ""}>
						<ExternalLink />
					</Link>{" "}
					<Link target="_blank" to={row.place_link ?? ""}>
						<img alt={"google maps"} className="w-[20px] h-[20px]" src={GoogleMapsIcon} />
					</Link>
				</div>
			),
		},
		{
			title: "type",
			uid: "type",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: IMapResponseItem) => <div className="flex items-center">{row.type}</div>,
		},
		{
			title: "website",
			uid: "website",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: IMapResponseItem) => <div className="flex items-center">{row.website}</div>,
		},
	];

	const handleDeleteModal = (row: Map | Job) => {
		setShowDeleteModal(true);
		setSelectedMap(row);
	};

	const handleDeleteMap = async () => {
		try {
			if (list.sourceType === ListSourceType.MAPS) {
				await deleteMap({ id: selectedMap?.id ?? 0, listId: list.id }).unwrap();
			}

			if (list.sourceType === ListSourceType.JOBS) {
				await deleteJob({ id: selectedMap?.id ?? 0, listId: list.id }).unwrap();
			}

			if (list.sourceType === ListSourceType.LI_PROFILE_VISITS) {
				await deleteLiProfileVisits({ id: selectedMap?.id ?? 0 }).unwrap();
			}

			if (list.sourceType === ListSourceType.LI_EVENTS) {
				await deleteLiEvent({ id: selectedMap?.id ?? 0, listId: list.id }).unwrap();
			}

			if (list.sourceType === ListSourceType.CSV_ENRICHMENTS) {
				await deleteCsvEnrichment({ id: selectedMap?.id ?? 0, listId: list.id }).unwrap();
			}

			if (list.sourceType === ListSourceType.LI_POST) {
				await deleteLiPost({ id: selectedMap?.id ?? 0, listId: list.id }).unwrap();
			}

			if (list.sourceType === ListSourceType.LI_PROFILE) {
				await deleteLiProfile({ id: selectedMap?.id ?? 0, listId: list.id }).unwrap();
			}

			if (list.sourceType === ListSourceType.LI_COMPANY_ACTIVITY) {
				await deleteLiCompanyActivity({ id: selectedMap?.id ?? 0, listId: list.id }).unwrap();
			}

			if (list.sourceType === ListSourceType.LI_PEOPLE) {
				await deleteLiPeople({ id: selectedMap?.id ?? 0, listId: list.id }).unwrap();
			}

			if (list.sourceType === ListSourceType.LI_SN) {
				await deleteLiSn({ id: selectedMap?.id ?? 0, listId: list.id }).unwrap();
			}

			if (list.sourceType === ListSourceType.FUNDING_ROUNDS) {
				await deleteFundingMa({ id: selectedMap?.id ?? 0, listId: list.id }).unwrap();
			}

			if (list.sourceType === ListSourceType.LEAD_DATABASE) {
				await deleteSwarm({ id: selectedMap?.id ?? 0, listId: list.id }).unwrap();
			}

			if (list.sourceType === ListSourceType.COMPANY_ENRICH) {
				await deleteCompanyEnrich({ id: selectedMap?.id ?? 0, listId: list.id }).unwrap();
			}

			if (list.sourceType === ListSourceType.TRACKER) {
				await deleteTrackerFile(selectedMap?.id ?? 0).unwrap();
			}

			if (list.sourceType === ListSourceType.PARALLEL_AI) {
				await deleteParallelAi({ id: selectedMap?.id ?? 0, listId: list.id }).unwrap();
			}

			setShowDeleteModal(false);
			refreshList(list.id);
		} catch (err) {
			console.error(err);
		}
	};

	// Helper to get all tracker files from trackers
	const trackerFiles: TrackerFileInfo[] =
		list.trackers?.flatMap((tracker) => tracker.trackerFiles || []) || [];

	const isEmpty =
		(list.sourceType === ListSourceType.MAPS && (!list.maps || list.maps?.length === 0)) ||
		(list.sourceType === ListSourceType.FUNDING_ROUNDS &&
			(!list.fundingMa || list.fundingMa?.length === 0)) ||
		(list.sourceType === ListSourceType.LEAD_DATABASE &&
			(!list.swarms || list.swarms?.length === 0)) ||
		(list.sourceType === ListSourceType.JOBS && (!list.jobs || list.jobs?.length === 0)) ||
		(list.sourceType === ListSourceType.PARALLEL_AI &&
			(!list.parallelAi || list.parallelAi?.length === 0)) ||
		(list.sourceType === ListSourceType.LI_COMPANY_ACTIVITY &&
			(!list.liCompanyActivity || list.liCompanyActivity?.length === 0)) ||
		(list.sourceType === ListSourceType.LI_PEOPLE &&
			(!list.liPeople || list.liPeople?.length === 0)) ||
		(list.sourceType === ListSourceType.LI_EVENTS &&
			(!list.liEvents || list.liEvents?.length === 0)) ||
		(list.sourceType === ListSourceType.LI_PROFILE_VISITS &&
			(!list.liProfileVisits || list.liProfileVisits?.length === 0)) ||
		(list.sourceType === ListSourceType.LI_POST && (!list.liPosts || list.liPosts?.length === 0)) ||
		(list.sourceType === ListSourceType.CSV_ENRICHMENTS &&
			(!list.csvEnrichments || list.csvEnrichments?.length === 0)) ||
		(list.sourceType === ListSourceType.JOB_CHANGE &&
			(!list.csvEnrichments || list.csvEnrichments?.length === 0)) ||
		(list.sourceType === ListSourceType.TRACKER && trackerFiles.length === 0) ||
		(list.sourceType === ListSourceType.LI_SN && (!list.liSn || list.liSn?.length === 0)) ||
		(list.sourceType === ListSourceType.COMPANY_ENRICH &&
			(!list.companyEnriches || list.companyEnriches?.length === 0));

	// PREC-4118: for an upload list, "No sources added" alone reads as loss — the reporter of that
	// ticket took a list with 250 hand-added leads and no file for a list whose file had gone
	// missing. Say what the empty step actually means, and that the leads are unaffected.
	const isUploadSource =
		list.sourceType === ListSourceType.CSV_ENRICHMENTS ||
		list.sourceType === ListSourceType.JOB_CHANGE;

	if (isEmpty) {
		return (
			<div className="w-full mt-8 bg-brand-white p-4 rounded-xl border border-gray-200">
				<div className="text-smd mb-5">Added sources</div>
				<div className="text-sm bg-white p-4 rounded-xl border border-gray-200">
					<div className="text-center">No sources added</div>
					{isUploadSource && (
						<div className="mt-2 text-center text-xs text-text-meta">
							{t("list.noFile.sourcesStep")}
						</div>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className="w-full mt-8 bg-brand-white p-4 rounded-xl border border-gray-200">
			{isPreviewLoading && <Loader />}
			{renderMessageSnackbar()}
			<div className="text-smd mb-5">Added sources</div>
			<div className="w-full bg-white border border-divider rounded-xl">
				<table className="w-full">
					<thead className="border-b border-b-divider">
						{getCustomTableColumns({
							type: list.sourceType,
							handleSelect,
							handleDuplicate,
							handleDeleteModal,
							handleTrigger: canTriggerParallelAi ? handleTriggerParallelAi : undefined,
							handlePreviewModal,
							t,
						})?.map((column) => {
							return (
								<th
									key={column.uid}
									className="text-left text-table-header text-xs px-4 items-center h-[44px]"
								>
									{column.title}
								</th>
							);
						})}
					</thead>
					<tbody>
						{[
							...(list.maps || []),
							...(list.jobs || []),
							...(list.csvEnrichments || []),
							...(list.liEvents || []),
							...(list.liCompanyActivity || []),
							...(list.liPosts || []),
							...(list.liProfileVisits || []),
							...(list.liProfile || []),
							...(list.liPeople || []),
							...(list.liSn || []),
							...(list.fundingMa || []),
							...(list.swarms || []),
							...(list.parallelAi || []),
							...(list.companyEnriches || []),
							...trackerFiles,
						].map((item, index) => {
							return (
								<tr key={index} className="border-b border-b-divider">
									{getCustomTableColumns({
										type: list.sourceType,
										downloadIsLoading,
										handleDownload,
										handleTrackerDownload,
										handleSelect,
										handleDuplicate,
										handleDeleteModal,
										handleTrigger: canTriggerParallelAi ? handleTriggerParallelAi : undefined,
										handlePreviewModal,
										handleStatistics: (row) => setStatisticsSource({ id: row.id, name: row.name }),
										t,
									})?.map((column) => {
										return (
											<td
												key={column.uid}
												className="px-4 items-center h-[44px] text-sm font-medium"
											>
												{column.render(item)}
											</td>
										);
									})}
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			{!!statisticsSource && (
				<XlsStatisticsModal
					csvEnrichmentId={statisticsSource.id}
					handleClose={() => setStatisticsSource(null)}
					sourceName={statisticsSource.name}
				/>
			)}
			{showDeleteModal && (
				<Modal
					handleClose={() => setShowDeleteModal(false)}
					handleSave={handleDeleteMap}
					isLoading={
						isDeleteLoading ||
						jobIsDeleteLoading ||
						csvEnrichmentIsDeleteLoading ||
						liEventIsDeleteLoading ||
						liPostIsDeleteLoading ||
						liCompanyActivityIsDeleteLoading ||
						liProfileIsDeleteLoading ||
						LiPeopleIsDeleteLoading ||
						liProfileVisitsIsDeleteLoading ||
						liSnIsDeleteLoading ||
						parallelAiIsDeleteLoading
					}
					isOpened={showDeleteModal}
					submitButtonColor="error"
					submitButtonText={t("basics.delete")}
					title={ts("delete.title")}
				>
					<TextField
						fullWidth
						defaultValue={selectedMap?.name}
						inputProps={{ readOnly: true }}
						label={t("product.name")}
					/>
				</Modal>
			)}

			{isPreviewOpen && (
				<Modal
					handleClose={closePreview}
					isLoading={isDeleteLoading}
					isOpened={isPreviewOpen}
					size="md"
					submitButtonColor="error"
					submitButtonText={t("basics.delete")}
					title={"Preview"}
				>
					<Table
						columns={previewColumns}
						data={previewRows || []}
						hideFooter={true}
						hidePagination={true}
					/>
				</Modal>
			)}
		</div>
	);
};
